import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../AuthProvider';
import { useSubscription } from '../../../context/SubscriptionContext';
import { CAN, can } from '../../../utils/workspaceAccess';

/**
 * User icon component
 */
const UserIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

/**
 * SidebarUserFooter Component
 *
 * Displays user info and account controls at the bottom of the sidebar.
 */
const SidebarUserFooter = ({ user, onLogout }) => {
  const { workspaceId, workspaces } = useAuth();
  const { openPortal, isTrialing, trialEndsAt, status } = useSubscription();
  const currentRoom = (workspaces || []).find((w) => w.id === workspaceId);
  const paysForThisRoom = can(currentRoom?.role, CAN.manageBilling);
  const [menuOpen, setMenuOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const getMenuPosition = () => {
    if (!buttonRef.current) return { bottom: 0, left: 0 };
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      bottom: window.innerHeight - rect.top + 8,
      left: rect.left - 170,
    };
  };

  const menuPosition = menuOpen ? getMenuPosition() : null;

  return (
    <>
      <div className="sidebar-user">
        <button
          ref={buttonRef}
          className={`sidebar-user__button ${menuOpen ? 'sidebar-user__button--active' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          title={user?.email || 'Account'}
        >
          <div className="sidebar-user__avatar">
            <UserIcon size={15} />
          </div>
        </button>
      </div>

      {menuOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            className="sidebar-user__menu"
            style={{
              position: 'fixed',
              bottom: menuPosition.bottom,
              left: menuPosition.left,
            }}
          >
            {user?.email && (
              <div className="sidebar-user__menu-header">
                <span className="sidebar-user__menu-email">{user.email}</span>
              </div>
            )}

            {paysForThisRoom && isTrialing && trialEndsAt && (
              <div className="sidebar-user__menu-item sidebar-user__menu-item--badge">
                <span className="sidebar-user__trial-badge">
                  Trial ends {trialEndsAt.toLocaleDateString()}
                </span>
              </div>
            )}

            {paysForThisRoom && status === 'past_due' && (
              <div className="sidebar-user__menu-item sidebar-user__menu-item--badge">
                <span className="sidebar-user__warning-badge">
                  Payment issue - update billing
                </span>
              </div>
            )}

            {paysForThisRoom && (
              <>
                <div className="sidebar-user__menu-divider" />

                <button
                  className="sidebar-user__menu-item"
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    openPortal();
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <span>Manage subscription</span>
                </button>
              </>
            )}

            {onLogout && (
              <button
                className="sidebar-user__menu-item sidebar-user__menu-item--danger"
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onLogout();
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>Sign out</span>
              </button>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default SidebarUserFooter;
